import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, useAnimationControls, usePresence, useReducedMotion } from 'framer-motion'
import { MoreHorizontal, Pencil, Trash2 } from 'lucide-react'
import {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
} from '@/src/renderer/components/ui/context-menu'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/src/renderer/components/ui/dropdown-menu'
import { Button } from '@/src/renderer/components/ui/Button'
import type { Goal } from '@/src/renderer/lib/db/models'
import './GoalCard.scss'

/** Soft limits — generous enough not to feel like pressure (Grilling #12). */
const TITLE_MAX = 80
const DESCRIPTION_MAX = 250
/** Resting width of the centered editor — mirrors `.goal-card--expanded` SCSS. */
const EDITOR_WIDTH = 480

export interface GoalSubmitData {
  title: string
  description: string | null
}

interface CommonProps {
  authorName: string
  authorAvatarUrl: string
  /**
   * `false` while the user's display profile is still loading. Until it's
   * `true`, the footer shows a skeleton instead of the name/avatar — otherwise
   * the bundled default ("Nutzer" + default avatar) flashes in before the real
   * profile resolves. Mirrors the sidebar UserAvatar's `isLoading` suppression.
   */
  authorReady: boolean
}

interface CollapsedGoalCardProps extends CommonProps {
  mode: 'collapsed'
  goal: Goal
  /** Invisible slot-holder while this goal is expanded — inert. */
  placeholder?: boolean
  /**
   * Open the editor. Receives this goal's id and on-screen rect so the editor
   * can grow out of (and shrink back into) this exact box — geometry, no
   * scaling. Passing the id lets the parent share one stable callback across
   * all cards (so they can be memoized).
   */
  onOpen?: (goalId: string, rect: DOMRect) => void
  onDelete?: (goalId: string) => void
}

interface ExpandedGoalCardProps extends CommonProps {
  mode: 'expanded'
  expandedKind: 'edit' | 'create'
  /** The goal being edited (edit mode only). */
  goal?: Goal
  /** Rect the editor grows out of / shrinks back into (clicked card or CTA button). */
  sourceRect?: DOMRect | null
  /** Rect of the centering overlay layer — used to compute the geometry morph. */
  overlayRect?: DOMRect | null
  onSubmit: (data: GoalSubmitData) => void
  onClose: () => void
}

export type GoalCardProps = CollapsedGoalCardProps | ExpandedGoalCardProps

function formatDate(date: Date, language: string): string {
  return date.toLocaleDateString(language, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

/** Footer shared by both modes — creation date (left) + author (right). */
const GoalCardFooter: React.FC<{
  date: string
  authorName: string
  authorAvatarUrl: string
  authorReady: boolean
}> = ({ date, authorName, authorAvatarUrl, authorReady }) => (
  <div className="goal-card__footer">
    <span className="goal-card__date">{date}</span>
    {authorReady ? (
      <div className="goal-card__author">
        <span className="goal-card__author-name">{authorName}</span>
        <img className="goal-card__avatar" src={authorAvatarUrl} alt="" aria-hidden />
      </div>
    ) : (
      // Profile still loading — same-size skeletons, never the bundled default
      // name/avatar, so the real author doesn't visibly flash in over a default.
      <div className="goal-card__author goal-card__author--loading" aria-hidden>
        <span className="goal-card__author-name-skeleton" />
        <span className="goal-card__avatar-skeleton" />
      </div>
    )}
  </div>
)

/** Collapsed card as it sits in the grid. */
const CollapsedCard: React.FC<CollapsedGoalCardProps> = ({
  goal,
  authorName,
  authorAvatarUrl,
  authorReady,
  placeholder,
  onOpen,
  onDelete,
}) => {
  const { t, i18n } = useTranslation()
  const formattedDate = formatDate(goal.createdAt, i18n.language)
  const rootRef = useRef<HTMLDivElement>(null)

  // Hand the editor this card's exact on-screen box so it can morph in/out of
  // it via geometry (width/height/position) instead of a transform-scale.
  const open = () => {
    if (rootRef.current) onOpen?.(goal.id, rootRef.current.getBoundingClientRect())
  }

  const body = (
    <>
      <div className="goal-card__panel">
        <h3 className="goal-card__title">{goal.title}</h3>
        {goal.description && (
          <p className="goal-card__description">{goal.description}</p>
        )}
      </div>
      <GoalCardFooter date={formattedDate} authorName={authorName} authorAvatarUrl={authorAvatarUrl} authorReady={authorReady} />
    </>
  )

  // Placeholder keeps the grid slot occupied (same markup → same size) while
  // the real card is expanded into the overlay. Inert, no interactions.
  if (placeholder) {
    return (
      <div className="goal-card goal-card--placeholder" aria-hidden>
        {body}
      </div>
    )
  }

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <div
          ref={rootRef}
          className="goal-card"
          role="button"
          tabIndex={0}
          onClick={open}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              open()
            }
          }}
        >
          {body}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="goal-card__menu-button"
                aria-label={t('goals.card.menuLabel')}
                onClick={(e) => e.stopPropagation()}
              >
                <MoreHorizontal size={16} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
              <DropdownMenuItem onSelect={() => open()}>
                <Pencil size={16} />
                {t('goals.card.edit')}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => onDelete?.(goal.id)}>
                <Trash2 size={16} />
                {t('goals.card.delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </ContextMenuTrigger>

      <ContextMenuContent>
        <ContextMenuItem onSelect={() => open()}>
          <Pencil size={16} />
          {t('goals.card.edit')}
        </ContextMenuItem>
        <ContextMenuItem onSelect={() => onDelete?.(goal.id)}>
          <Trash2 size={16} />
          {t('goals.card.delete')}
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  )
}

/** Expanded card — the centered editor (edit or create). */
const ExpandedCard: React.FC<ExpandedGoalCardProps> = ({
  expandedKind,
  goal,
  authorName,
  authorAvatarUrl,
  authorReady,
  sourceRect,
  overlayRect,
  onSubmit,
  onClose,
}) => {
  const { t, i18n } = useTranslation()
  const reduced = useReducedMotion()
  const [title, setTitle] = useState(goal?.title ?? '')
  const [description, setDescription] = useState(goal?.description ?? '')

  const trimmedTitle = title.trim()
  const canSave = trimmedTitle.length > 0
  const displayDate = formatDate(goal?.createdAt ?? new Date(), i18n.language)
  const isCreate = expandedKind === 'create'

  const handleSave = () => {
    if (!canSave) return
    onSubmit({ title: trimmedTitle, description: description.trim() || null })
  }

  // ── Geometry morph (no scaling) ──────────────────────────────────────────
  // The editor grows out of `sourceRect` (the clicked card, or the CTA button
  // for create) into its centered resting state by animating WIDTH, HEIGHT and
  // POSITION only — never `transform: scale`, so text and buttons never warp.
  //
  // The card is flex-centered in the overlay layer, so its center stays pinned
  // to the overlay center at any width/height. We therefore only translate the
  // delta between the source's center and the overlay's center.
  //
  // Height is animated to an EXACT measured pixel value, never `height: 'auto'`:
  // framer re-measures `auto` at the end of the tween, and that value differs a
  // px or two from the running animation — which nudged the card taller on the
  // final frame (the title visibly jumping up). Measuring the natural height
  // ourselves and animating to that px removes the end-of-animation jump.
  const cardRef = useRef<HTMLDivElement>(null)
  const controls = useAnimationControls()
  const [isPresent, safeToRemove] = usePresence()

  const targetWidth = overlayRect ? Math.min(EDITOR_WIDTH, overlayRect.width - 32) : EDITOR_WIDTH
  const fromGeometry =
    sourceRect && overlayRect
      ? {
          x: sourceRect.left + sourceRect.width / 2 - (overlayRect.left + overlayRect.width / 2),
          y: sourceRect.top + sourceRect.height / 2 - (overlayRect.top + overlayRect.height / 2),
          width: sourceRect.width,
          height: sourceRect.height,
        }
      : null

  const morphTransition = { duration: 0.32, ease: [0.4, 0, 0.2, 1] }

  // Open. The card first paints at its natural size (CSS width, auto height) so
  // we can measure the exact target height; then we snap it back to the source
  // box (still before paint, so no flash) and animate out to that measured px.
  useLayoutEffect(() => {
    const el = cardRef.current
    if (!el) return
    if (reduced || !fromGeometry) {
      controls.set({ opacity: 0 })
      void controls.start({ opacity: 1, transition: { duration: reduced ? 0 : 0.18 } })
      return
    }
    const naturalHeight = el.offsetHeight
    controls.set({
      x: fromGeometry.x,
      y: fromGeometry.y,
      width: fromGeometry.width,
      height: fromGeometry.height,
      opacity: isCreate ? 0 : 1,
    })
    void controls.start({
      x: 0,
      y: 0,
      width: targetWidth,
      height: naturalHeight,
      opacity: 1,
      transition: morphTransition,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Close. AnimatePresence flips `isPresent` to false → shrink back into the
  // source box, then release the node for unmount.
  useEffect(() => {
    if (isPresent) return
    let cancelled = false
    const animateOut = async () => {
      if (!reduced && fromGeometry) {
        await controls.start({
          x: fromGeometry.x,
          y: fromGeometry.y,
          width: fromGeometry.width,
          height: fromGeometry.height,
          opacity: isCreate ? 0 : 1,
          transition: morphTransition,
        })
      } else {
        await controls.start({ opacity: 0, transition: { duration: reduced ? 0 : 0.18 } })
      }
      if (!cancelled) safeToRemove?.()
    }
    void animateOut()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPresent])

  return (
    <motion.div
      ref={cardRef}
      // `--closing` (while exiting) eases the lifted shadow back down to the
      // collapsed card's resting shadow, so it doesn't snap at the unmount.
      className={`goal-card goal-card--expanded${isPresent ? '' : ' goal-card--closing'}`}
      role="dialog"
      aria-modal="true"
      aria-label={t(isCreate ? 'goals.cta.empty' : 'goals.card.edit')}
      onClick={(e) => e.stopPropagation()}
      // Hard floor: the card may never render shorter than the source box — a
      // safety net for the morph's start/end frames.
      style={!reduced && fromGeometry ? { minHeight: fromGeometry.height } : undefined}
      animate={controls}
    >
      <div className="goal-card__panel">
        <input
          className="goal-card__title-input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={t('goals.editor.titlePlaceholder')}
          maxLength={TITLE_MAX}
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              handleSave()
            }
          }}
        />

        <textarea
          className="goal-card__description-input"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t('goals.editor.descriptionPlaceholder')}
          maxLength={DESCRIPTION_MAX}
        />

        <motion.div
          className="goal-card__actions"
          // The buttons are the part that's "added" by the editor — fade them
          // in slightly later so the card reads as title + description first,
          // then grows the action bar in.
          initial={reduced ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={reduced ? { duration: 0 } : { delay: 0.16, duration: 0.2 }}
        >
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={!canSave}
            shortcut={{ variant: 'return' }}
          >
            {t('goals.editor.save')}
          </Button>
          <Button
            variant="secondary"
            onClick={onClose}
            shortcut={{ variant: 'character', text: t('goals.editor.escHint') }}
          >
            {t('goals.editor.cancel')}
          </Button>
        </motion.div>
      </div>

      <GoalCardFooter date={displayDate} authorName={authorName} authorAvatarUrl={authorAvatarUrl} authorReady={authorReady} />

      {/* The collapsed card's ⋯ button, kept in the DOM through the morph (edit
          only) so it fades out via opacity instead of being yanked out — the
          abrupt removal otherwise reads as the card's top shrinking. Purely
          decorative here: non-interactive, hidden from a11y. */}
      {!isCreate && (
        <motion.div
          className="goal-card__menu-button"
          aria-hidden
          style={{ pointerEvents: 'none' }}
          initial={reduced ? false : { opacity: 1 }}
          animate={{ opacity: 0 }}
          transition={reduced ? { duration: 0 } : { duration: 0.15 }}
        >
          <MoreHorizontal size={16} />
        </motion.div>
      )}
    </motion.div>
  )
}

/**
 * A goal as a card. Two modes:
 *  - `collapsed` — sits in the grid; click morphs it open.
 *  - `expanded`  — the centered editor (edit an existing goal, or create).
 *
 * The open/close morph animates geometry (width/height/position) only — never
 * a transform-scale — so the card's contents never distort.
 *
 * A goal is NOT a task — no checkbox, no completion. See CONTEXT.md.
 */
const GoalCardComponent: React.FC<GoalCardProps> = (props) => {
  return props.mode === 'collapsed' ? <CollapsedCard {...props} /> : <ExpandedCard {...props} />
}

// Memoized so grid cards don't re-render on every GoalsView render (open/close,
// dim toggle, exiting-slot bookkeeping). Effective because GoalsView passes
// stable callbacks (openEdit / handleDelete) and `useStableLiveQuery` keeps the
// reference of an unchanged goal stable.
export const GoalCard = React.memo(GoalCardComponent)
